import React from 'react'
import { useTranslation } from 'react-i18next'
import { Avatar, type PieChartDataPoint } from '@node-space/ui-components'
import { Portfolio } from 'types/rewards'

export interface RewardsSummaryData {
  totalBalance: number
  totalEarnings: number
  totalPendingEarnings: number
  portfoliosSummary: PieChartDataPoint[]
}

const defaultRewardsSummaryData: RewardsSummaryData = {
  totalBalance: 0,
  totalEarnings: 0,
  totalPendingEarnings: 0,
  portfoliosSummary: [],
}

const USDC_CHART_COLOR = 'var(--primary-500)'
const USDG_CHART_COLOR = 'var(--primary-400)'
const USDC_PENDING_CHART_COLOR = 'var(--primary-300)'
const USDG_PENDING_CHART_COLOR = 'var(--primary-200)'

export const useRewardsSummary = (portfolios: Portfolio[]): RewardsSummaryData => {
  const { t } = useTranslation()

  if (!portfolios || portfolios.length === 0) {
    return defaultRewardsSummaryData
  }

  return portfolios.reduce((acc, portfolio) => {
    const balance = Number.parseFloat(portfolio?.balance ?? '0') || 0
    const earnings = Number.parseFloat(portfolio?.totalEarnedAmount ?? '0') || 0

    // API doesn't return value for blocked anymore, probably to be remove
    const blocked = 0

    const color = portfolio.currency === 'USDC' ? USDC_CHART_COLOR : USDG_CHART_COLOR
    const pendingColor =
      portfolio.currency === 'USDC' ? USDC_PENDING_CHART_COLOR : USDG_PENDING_CHART_COLOR

    return {
      totalBalance: acc.totalBalance + balance,
      totalEarnings: acc.totalEarnings + earnings,
      totalPendingEarnings: acc.totalPendingEarnings + blocked,
      portfoliosSummary: [
        ...acc.portfoliosSummary,
        {
          name: portfolio.currency,
          value: balance,
          color,
          Avatar: <Avatar size="small" type="currency" name={portfolio.currency} />,
          tooltipLabel: t('rewards.allocated'),
        },
        ...(blocked > 0
          ? [
              {
                name: portfolio.currency,
                value: blocked,
                color: pendingColor,
                Avatar: <Avatar size="small" type="currency" name={portfolio.currency} />,
                tooltipLabel: t('rewards.pending'),
              },
            ]
          : []),
      ],
    }
  }, defaultRewardsSummaryData)
}
